import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FormField } from '@/shared/components/form';
import { formatDateTime } from '@/lib/utils';
import { useSaveProposalTemplate, useValidateProposalTemplate } from '../../hooks/useProposalTemplates';
import { errorCode, errorMessage, MAY_STILL_FINISH } from '../../lib/errors';
import { slidesUrl } from '../../lib/format';
import type { Lang, ProposalTemplate, TemplateValidation } from '../../types';

function ValidationReport({ report }: { report: TemplateValidation }) {
  const { t } = useTranslation('crm');
  if (!report.errors.length && !report.warnings.length) {
    return <p className="text-xs text-success-dark">{t('proposalTemplates.report.ready', { slides: report.slideCount })}</p>;
  }
  return (
    <div className="space-y-2 text-xs">
      {report.errors.length > 0 && (
        <div className="text-destructive">
          <p className="font-semibold">{t('proposalTemplates.report.errors')}</p>
          <ul className="list-disc pl-4">{report.errors.map((e, i) => <li key={`${e.code}-${i}`}>{e.message}</li>)}</ul>
        </div>
      )}
      {report.warnings.length > 0 && (
        <div className="text-warning-dark">
          <p className="font-semibold">{t('proposalTemplates.report.warnings')}</p>
          <ul className="list-disc pl-4">{report.warnings.map((w, i) => <li key={`${w.code}-${i}`}>{w.message}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/** One offer × language slot: the registered Slides file, its last contract check, and replace / check again. */
export function TemplateSlotCard({ language, template }: { language: Lang; template: ProposalTemplate | undefined }) {
  const { t } = useTranslation('crm');
  const save = useSaveProposalTemplate();
  const validate = useValidateProposalTemplate();
  const [link, setLink] = useState('');
  const [name, setName] = useState('');
  const busy = save.isPending || validate.isPending;
  const error = save.error ?? validate.error;
  const code = error ? errorCode(error) : null;
  const title = t(`proposalTemplates.slot.${language}`);

  const onSave = () => save.mutate({ language, driveFileId: link.trim(), name }, {
    onSuccess: ({ validation }) => {
      setLink('');
      setName('');
      if (validation.errors.length) toast.warning(t('proposalTemplates.savedWithErrors', { n: validation.errors.length }));
      else toast.success(t('proposalTemplates.saved'));
    },
  });

  return (
    <Card role="region" aria-label={title}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {template ? (
          <div className="space-y-1">
            <a href={slidesUrl(template.driveFileId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
              {template.name ?? template.driveFileId} <ExternalLink className="h-3 w-3" />
            </a>
            <p className="text-xs text-muted-foreground">
              {template.lastValidatedAt ? t('proposalTemplates.lastChecked', { when: formatDateTime(template.lastValidatedAt) }) : t('proposalTemplates.neverChecked')}
            </p>
            {template.lastValidation && <ValidationReport report={template.lastValidation} />}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => validate.mutate(template.id)}>
              {validate.isPending ? t('proposalTemplates.checking') : t('proposalTemplates.validate')}
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground">{t('proposalTemplates.empty')}</p>
        )}
        <div className="space-y-3 border-t pt-3">
          <FormField label={t('proposalTemplates.link')} htmlFor={`tpl-${language}-link`} hint={t('proposalTemplates.linkHint')}>
            <Input id={`tpl-${language}-link`} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://docs.google.com/presentation/d/…/edit" />
          </FormField>
          <FormField label={t('proposalTemplates.name')} htmlFor={`tpl-${language}-name`} hint={t('proposalTemplates.nameHint')}>
            <Input id={`tpl-${language}-name`} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <Button size="sm" disabled={busy || !link.trim()} onClick={onSave}>
            {save.isPending ? t('proposalTemplates.saving') : template ? t('proposalTemplates.replace') : t('proposalTemplates.save')}
          </Button>
        </div>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{code && MAY_STILL_FINISH.has(code) ? t('proposalTemplates.timeout') : errorMessage(error)}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
